import { useEffect, useMemo, useState } from 'react'
import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useCurrentOrganization } from '@/shared/hooks/api/organization'
import {
  type PackedItemsOverviewExportQuery,
  type PackedItemsOverviewItem,
  type PackedItemsOverviewSortBy,
  type PackedItemsOverviewSortDir,
  type PageInfo,
} from '@/shared/hooks/api/packedItemsOverview'
import { downloadPackedItemsOverviewExport } from '@/shared/hooks/api/packedItemsOverview'
import {
  type NavigateFn,
  useTableUrlState,
} from '@/shared/hooks/use-table-url-state'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTableColumnHeader, DataTablePagination } from '@/components/data-table'
import { ExportXlsxButton } from '@/features/packaging-suppliers/export/ExportXlsxButton'
import { buildPackagingExportFilename } from '@/features/packaging-suppliers/export/packagingExportFilename'
import { PackagedItemRedFlagsCell } from './PackagedItemRedFlagsCell'
import { PackagingComplianceProgressBadge } from './PackagingComplianceProgressBadge'
import { PackedItemSkuCell } from './PackedItemSkuCell'
import { PackedItemsSkuFilterCombobox } from './PackedItemsSkuFilterCombobox'

const packedItemsTableColumns = [
  { width: '28%' },
  { width: '14%' },
  { width: '28%' },
  { width: '22%' },
  { width: '8%' },
] as const

function PackedItemsTableColGroup() {
  return (
    <colgroup>
      {packedItemsTableColumns.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

type PackagedItemsOverviewTableProps = {
  items: PackedItemsOverviewItem[]
  pageInfo: PageInfo
  exportQuery: PackedItemsOverviewExportQuery
  isFetching?: boolean
  search: Record<string, unknown>
  navigate: NavigateFn
  sortBy: PackedItemsOverviewSortBy
  sortDir: PackedItemsOverviewSortDir
}

export function PackagedItemsOverviewTable({
  items,
  pageInfo,
  exportQuery,
  isFetching = false,
  search,
  navigate,
  sortBy,
  sortDir,
}: PackagedItemsOverviewTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const { data: organization } = useCurrentOrganization()

  const initialQuery = typeof search.q === 'string' ? search.q : ''
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    initialQuery.trim()
  )

  const sorting = useMemo<SortingState>(
    () => [{ id: sortBy, desc: sortDir === 'desc' }],
    [sortBy, sortDir]
  )

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    const primary = next[0]
    const nextSortBy =
      primary?.id != null && primary.id !== ''
        ? (primary.id as PackedItemsOverviewSortBy)
        : 'name'
    const nextSortDir: PackedItemsOverviewSortDir = primary?.desc
      ? 'desc'
      : 'asc'
    navigate({
      search: (previous) => ({
        ...previous,
        sortBy: nextSortBy === 'name' ? undefined : nextSortBy,
        sortDir: nextSortDir === 'asc' ? undefined : nextSortDir,
        page: undefined,
      }),
    })
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim())
    }, 300)
    return () => {
      window.clearTimeout(handle)
    }
  }, [searchQuery])

  const {
    pagination,
    onPaginationChange,
    ensurePageInRange,
    columnFilters,
    onColumnFiltersChange,
  } = useTableUrlState({
    search,
    navigate,
    pagination: { defaultPage: 1, defaultPageSize: 20 },
    columnFilters: [{ columnId: 'sku', searchKey: 'sku', type: 'string' }],
  })

  const skuFromUrl = useMemo(() => {
    const found = columnFilters.find((filter) => filter.id === 'sku')
    if (typeof found?.value === 'string') {
      return found.value.trim()
    }
    return ''
  }, [columnFilters])

  useEffect(() => {
    const currentQuery = typeof search.q === 'string' ? search.q.trim() : ''
    if (debouncedSearchQuery === currentQuery) {
      return
    }
    navigate({
      search: (previous) => {
        const next = { ...previous }
        if (debouncedSearchQuery === '') {
          delete next.q
        } else {
          next.q = debouncedSearchQuery
        }
        next.page = 1
        return next
      },
    })
  }, [debouncedSearchQuery, navigate, search.q])

  const skuComboboxItems = useMemo(() => {
    if (skuFromUrl === '') {
      return []
    }
    return [skuFromUrl]
  }, [skuFromUrl])

  const handleSkuFilterChange = (sku: string | null) => {
    const next = sku?.trim() ?? ''
    onColumnFiltersChange((previous) => {
      const withoutSku = previous.filter((filter) => filter.id !== 'sku')
      if (next === '') {
        return withoutSku
      }
      return [...withoutSku, { id: 'sku', value: next }]
    })
  }

  const columns = useMemo<ColumnDef<PackedItemsOverviewItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('list.columnName')} />
        ),
        cell: ({ row }) => (
          <Link
            to={`/packaging/packed-items/${row.original.id}`}
            className='block truncate text-foreground underline decoration-dotted underline-offset-2'
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'sku',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('packedItems.columnSku')} />
        ),
        cell: ({ row }) => (
          <PackedItemSkuCell
            sku={row.original.sku}
            notInSourceList={row.original.notInSourceList}
            fromPartsCatalog={row.original.fromPartsCatalog}
          />
        ),
      },
      {
        id: 'supplier',
        accessorFn: (row) => row.supplierName,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('packedItems.columnSupplier')}
          />
        ),
        cell: ({ row }) => (
          <Link
            to={`/packaging/suppliers/${row.original.supplierId}`}
            className='block truncate text-foreground underline-offset-4 hover:underline'
          >
            {row.original.supplierName}
          </Link>
        ),
      },
      {
        id: 'complianceProgress',
        accessorFn: (row) => {
          if (row.totalPackagingItemCount === 0) {
            return 0
          }
          return row.completedPackagingItemCount / row.totalPackagingItemCount
        },
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('list.columnComplianceProgress')}
          />
        ),
        cell: ({ row }) => (
          <PackagingComplianceProgressBadge
            completed={row.original.completedPackagingItemCount}
            total={row.original.totalPackagingItemCount}
          />
        ),
      },
      {
        id: 'audit',
        accessorFn: (row) => row.redFlags.length,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('list.columnAudit')} />
        ),
        cell: ({ row }) => (
          <PackagedItemRedFlagsCell
            redFlags={row.original.redFlags}
            redFlagDetails={row.original.redFlagDetails}
            packagingItems={row.original.packagingItems}
          />
        ),
      },
    ],
    [t]
  )

  const table = useReactTable({
    data: items,
    columns,
    state: {
      columnFilters,
      pagination,
      sorting,
    },
    onColumnFiltersChange,
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: pageInfo.totalPages,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    ensurePageInRange(pageInfo.totalPages)
  }, [ensurePageInRange, pageInfo.totalPages])

  const hasActiveFilters =
    debouncedSearchQuery !== '' || skuFromUrl !== ''

  const exportFileName = useMemo(
    () =>
      buildPackagingExportFilename({
        date: new Date(),
        organizationName: organization?.name,
        kind: 'packed-items',
      }),
    [organization?.name]
  )

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='flex shrink-0 flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
        <div className='flex w-full max-w-3xl flex-col gap-2 xl:flex-row xl:items-center'>
          <InputGroup className='h-8 w-full max-w-52 shrink-0'>
            <InputGroupAddon align='inline-start'>
              <Search aria-hidden />
            </InputGroupAddon>
            <InputGroupInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('packedItems.searchPlaceholder')}
            />
          </InputGroup>

          <PackedItemsSkuFilterCombobox
            id='packed-items-sku-filter'
            className='w-full max-w-48 shrink-0'
            items={skuComboboxItems}
            value={skuFromUrl === '' ? null : skuFromUrl}
            onValueChange={handleSkuFilterChange}
            placeholder={t('packedItems.skuFilterPlaceholder')}
            searchPlaceholder={t('packedItems.skuFilterSearchPlaceholder')}
            emptyText={t('packedItems.skuFilterEmpty')}
            typeToSearchText={t('packedItems.skuFilterTypeToSearch')}
            refineSearchText={t('packedItems.skuFilterRefineSearch')}
          />
        </div>

        <div className='flex shrink-0 items-center justify-end gap-4'>
          <ExportXlsxButton
            download={async () => {
              await downloadPackedItemsOverviewExport(
                exportQuery,
                exportFileName
              )
            }}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      </div>

      <div
        className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border'
        aria-busy={isFetching}
      >
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='w-full min-w-full table-fixed caption-bottom text-sm'>
            <PackedItemsTableColGroup />
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className='group/row'>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={
                        header.column.id === 'complianceProgress'
                          ? 'sticky top-0 z-10 bg-background whitespace-normal group-hover/row:bg-muted'
                          : 'sticky top-0 z-10 bg-background group-hover/row:bg-muted'
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className='group/row'>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={
                          cell.column.id === 'complianceProgress' ||
                          cell.column.id === 'audit'
                            ? 'bg-background group-hover/row:bg-muted'
                            : 'max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted'
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className='h-24 text-center text-muted-foreground'
                  >
                    {t('packedItems.notFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
        <DataTablePagination
          table={table}
          className='shrink-0 border-t bg-background py-3'
        />
      </div>
    </div>
  )
}
